import { ViewController } from '@estee/elc-service';
import { computed, observable, action } from 'mobx';
import { diContainer } from '../../service-setup/diContainer';
import serviceNames from '~setup/ProductService';
import { root } from '@estee/elc-universal-utils';
import { events, emit, on, query, serviceQueries } from '@estee/elc-service-bus';
import { IAsset } from '../domain/entities/Asset';
import {
    ICarouselSettings,
    IImageCarouselSettings,
    IImageDevice,
    IImagesSizes
} from '../interfaces/IProdcat';
import { ComponentTypes } from '../constants/ComponentTypes';
import { LiveChatComponentTypes } from '../constants/LiveChatComponentTypes';
import { AssetTypes } from '../constants/AssetTypes';
import { DeviceType, DESKTOP, MOBILE } from '../constants/DeviceTypes';
import { Product } from '../domain/entities/Product';
import { Sku } from '../domain/entities/Sku';
import { IVideoAsset } from '../domain/entities/VideoAsset';
import { IMediaType } from '../domain/entities/Media';
import * as queryParamUtils from '../utils/QueryParams';
import { ProductSelectedUseCase } from '../use-cases/ProductSelectedUseCase';
import { buildIngredientsContent } from '~utils/ContentFormatter';
import { IProductDetails } from '~interfaces/IProductFull';
import { Shade } from '~domain/entities/Shade';
import { Breakpoints } from '@estee/elc-base-theme';

export interface IMediaObject {
    media: IMediaType[];
    defaultImage: IAsset;
}

export interface IImagesForDevice {
    [key: string]: IMediaObject;
}

export interface IAlternateImagesForDevice {
    [key: string]: IAsset;
}

interface IFavorites {
    skuBaseIds: number[];
}

interface IFavoriteUpdatedEventData {
    skuBaseId: number;
    isFavorite: boolean;
    isAlreadyAdded?: boolean;
}

export interface IProductControllerData extends IProductDetails {
    largeImages: IMediaType[];
    product: Product;
    shade: Shade;
    selectedSku: Sku;
    productUrl: string;
    displayName: string;
    productBadge: string;
    skuBadge: string;
    imageCarouselSettings: IImageCarouselSettings;
    selectedAutoReplenishOption: number;
    smallImages: IMediaType[];
    largeMedia: IMediaType[];
    smallMedia: IMediaType[];
    isLoaded: boolean;
    selectedProductUrl: string;
    isProductImageEnabled: boolean;
    charLimit?: number;
    productTruncatedDescription?: string;
    shouldRenderReadMoreLink?: boolean;
    isProductQuickViewOpen?: boolean;
    isVtoMakeupEnabled?: boolean;
    isVtoFoundationEnabled?: boolean;
    hasShadeSidebarVto?: boolean;
    isSmartGiftEnabled?: boolean;
    howDoesItWorkLabel?: string;
    currentTabId?: string;
    isDescriptionExpanded?: boolean;
    isOverlayOpen?: boolean;
    closeOverlay?(): void;
    openOverlay?(): void;
    closeModalAction?(): void;
    overlayTitle?: string;
    closeOnOverlayMaskClick?: boolean;
    overlayClassNames?: string;
    // should get deprecated
    isShortDescriptionEnabled?: boolean;
    productAttribute?: string;
    lazyLoadImageDesktop?: boolean;
    lazyLoadImageMobile?: boolean;
}

export abstract class ProductBaseController extends ViewController {
    public static shadeUrlParam = 'shade';
    public static sizeUrlParam = 'size';
    public positionIndex: number;

    @observable public isLoaded: boolean;
    @observable product: Product;
    @observable selectedSkuId: string;
    @observable public selectedAutoReplenishOption: number = 0;
    @observable public carouselSettings?: ICarouselSettings;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @observable private prodcatConfig: any;
    @observable public isProductImageEnabled: boolean = true;
    @observable public isDescriptionExpanded: boolean = false;

    constructor() {
        super();
        this.prodcatConfig = diContainer.get(serviceNames.config);
    }

    public abstract get data(): IProductControllerData;

    @computed
    public get baseData(): IProductControllerData {
        return {
            ...this.productDetails,
            product: this.product,
            shade: this.selectedSku.selectedShade,
            largeImages: this.largeImages,
            selectedSku: this.selectedSku,
            productUrl: this.selectedProductUrl,
            selectedProductUrl: this.selectedProductUrl,
            displayName: this.product.displayName,
            productBadge: this.product.productBadge,
            skuBadge: this.selectedSku.skuBadge,
            imageCarouselSettings: this.imageCarouselSettings,
            selectedAutoReplenishOption: this.selectedAutoReplenishOption,
            smallImages: this.smallImages,
            smallMedia: this.smallMedia,
            largeMedia: this.largeMedia,
            isLoaded: this.isLoaded,
            isDescriptionExpanded: this.isDescriptionExpanded,
            isProductImageEnabled: this.isProductImageEnabled
        };
    }

    @action
    public setProduct(product: Product) {
        this.product = product;
    }

    @action
    public setSelectedSkuId(skuId: string) {
        this.selectedSkuId = skuId;
    }

    @computed
    public get imagesSizeConfig() {
        const defaultImagesSizesConfig: IImagesSizes = {
            productBrief: {
                desktop: AssetTypes.MEDIUM,
                mobile: AssetTypes.LARGE
            },
            productQuickshop: {
                desktop: AssetTypes.LARGE,
                mobile: AssetTypes.LARGE
            },
            productFull: {
                desktop: AssetTypes.LARGE,
                mobile: AssetTypes.LARGE
            }
        };

        return this.prodcatConfig &&
            this.prodcatConfig.config &&
            this.prodcatConfig.config.imagesSizes
            ? this.prodcatConfig.config.imagesSizes
            : defaultImagesSizesConfig;
    }

    @computed
    public get selectedSku(): Sku {
        return (
            this.product.skus.find((sku) => sku.skuId === this.selectedSkuId) ||
            this.product.defaultSku
        );
    }

    @computed
    public get productDetails(): IProductDetails {
        const {
            whoItsFor: { content: whoItsFor },
            greatToKnow: { content: greatToKnow },
            whatItIs: { content: whatItIs },
            whatItDoes: { content: whatItDoes },
            keyIngredient,
            howDoesItWork: { content: howDoesItWork },
            howToUse: { content: howToUse },
            fragranceKeyNotes
        } = this.product;
        const allIngredients = buildIngredientsContent(this.selectedSku);

        return {
            whoItsFor,
            greatToKnow,
            whatItIs,
            whatItDoes,
            keyIngredient,
            allIngredients,
            howDoesItWork,
            howToUse,
            fragranceKeyNotes
        };
    }

    @computed
    get largeImages() {
        let skuImages: IAsset[] = [];
        let productLargeImages: IAsset[] = [];
        const { product, selectedSku } = this;

        if (selectedSku) {
            if (selectedSku.hasLargeImages) {
                [...skuImages] = selectedSku.largeImages;
            }
        }

        if (product && product.hasLargeImages) {
            [...productLargeImages] = product.largeImages;
        }

        return this.enhanceImageListAltText([...skuImages, ...productLargeImages]);
    }

    @computed
    public get smallImages() {
        // use large product images for carousel until we have small images
        return this.largeImages;
    }

    @computed
    public get smallMedia() {
        // use large product images for carousel until we have small images
        const videoAltEnabled = this.carouselSettings && this.carouselSettings.videoAltEnabled;

        if (!videoAltEnabled) {
            return this.smallImages;
        }

        return this.addVideoThumbnails(this.smallImages, this.product.media.videos || []);
    }

    @computed
    get largeMedia() {
        const videoAltEnabled = this.carouselSettings && this.carouselSettings.videoAltEnabled;

        if (!videoAltEnabled) {
            return this.largeImages;
        }

        const media: IMediaType[] = this.addVideoThumbnails(
            this.largeImages,
            this.product.media.videos || []
        );

        return media;
    }

    @computed
    get mediumImages() {
        let skuImages: IAsset[] = [];
        let productMediumImages: IAsset[] = [];
        const { product } = this;
        const selectedSku = this.selectedSku;

        if (selectedSku) {
            if (selectedSku.hasMediumImages) {
                [...skuImages] = selectedSku.mediumImages;
            }
        }

        if (product && product.hasMediumImages) {
            [...productMediumImages] = product.mediumImages;
        }

        return this.enhanceImageListAltText([...skuImages, ...productMediumImages]);
    }

    @computed
    public get altTextPrefix(): string {
        return this.selectedSku.selectedShade.name || this.selectedSku.selectedSize.value || '';
    }

    @computed
    public get imageCarouselSettings() {
        return (
            this.prodcatConfig &&
            this.prodcatConfig.config &&
            this.prodcatConfig.config.imageCarouselSettings
        );
    }

    @computed
    public get shortDescriptionSettings() {
        return (
            this.prodcatConfig &&
            this.prodcatConfig.config &&
            this.prodcatConfig.config.shortDescription
        );
    }

    public productUpdateQuantity = (quantity: number) => {
        this.product.setQuantity(quantity);
    };

    private getImagesForDevice(device: DeviceType, imagesSizeConfig?: IImageDevice): IMediaObject {
        const useLargeImages = imagesSizeConfig && imagesSizeConfig[device] === AssetTypes.LARGE;
        const selectedSku = this.selectedSku;

        const media: IMediaType[] = useLargeImages ? this.largeImages : this.mediumImages;
        const defaultImage = useLargeImages
            ? this.enhanceImageAltText(selectedSku.defaultLargeImage)
            : this.enhanceImageAltText(selectedSku.defaultMediumImage);

        return {
            media,
            defaultImage
        };
    }

    public getCustomSizeAlternateImage = (component: ComponentTypes): IAlternateImagesForDevice => {
        const imagesSizeConfig = this.imagesSizeConfig && this.imagesSizeConfig[component];

        return {
            desktop: this.getAlternateImageForDevice(DESKTOP, imagesSizeConfig),
            mobile: this.getAlternateImageForDevice(MOBILE, imagesSizeConfig)
        };
    };

    private getAlternateImageForDevice(
        device: DeviceType,
        imagesSizeConfig?: IImageDevice
    ): IAsset {
        const useLargeImages = imagesSizeConfig && imagesSizeConfig[device] === AssetTypes.LARGE;

        return useLargeImages
            ? this.selectedSku.defaultAlternateLargeImage
            : this.selectedSku.defaultAlternateMediumImage;
    }

    private getMediaForDevice(device: DeviceType, imagesSizeConfig?: IImageDevice): IMediaObject {
        const imagesData = this.getImagesForDevice(device, imagesSizeConfig);
        const videoAltEnabled = this.carouselSettings && this.carouselSettings.videoAltEnabled;

        if (!videoAltEnabled) {
            return imagesData;
        }

        const media: IMediaType[] = this.addVideoThumbnails(
            imagesData.media,
            this.product.media.videos || []
        );

        return {
            defaultImage: imagesData.defaultImage,
            media
        };
    }

    public getCustomSizeMedia = (component: ComponentTypes): IImagesForDevice => {
        const imagesSizeConfig = this.imagesSizeConfig && this.imagesSizeConfig[component];

        return {
            desktop: this.getMediaForDevice(DESKTOP, imagesSizeConfig),
            mobile: this.getMediaForDevice(MOBILE, imagesSizeConfig)
        };
    };

    public addVideoThumbnails(mediaList: IMediaType[], productVideos: IVideoAsset[]) {
        if (!productVideos || !productVideos.length) {
            return mediaList;
        }

        const videoPositioningEnabled =
            this.carouselSettings && this.carouselSettings.videoPositioningEnabled;

        const videoReducer = videoPositioningEnabled
            ? this.insertVideoAtPosition
            : this.insertVideoAtEnd;

        return productVideos.reduce(videoReducer, mediaList);
    }

    private insertVideoAtEnd(mediaList: IMediaType[], productVideo: IVideoAsset) {
        return [...mediaList, productVideo];
    }

    private insertVideoAtPosition(mediaList: IMediaType[], productVideo: IVideoAsset) {
        const _mediaList = [...mediaList];
        let position = productVideo.position != null ? productVideo.position : mediaList.length;

        if (position > mediaList.length) {
            position = mediaList.length;
        }

        _mediaList.splice(position, 0, productVideo);

        return _mediaList;
    }

    @action
    public setMediaSettings = (mediaSettings?: ICarouselSettings) => {
        this.carouselSettings = mediaSettings;
    };

    @action
    public setAutoReplenishOption = (option: number) => {
        this.selectedAutoReplenishOption = option;
    };

    @action
    public selectShade = (name: string) => {
        const clickedShade = this.product.getShadeByName(name);
        if (clickedShade && clickedShade.parentSku) {
            this.selectedSkuId = clickedShade.parentSku.skuId;
        }
    };

    @action
    public selectSize = (label: string) => {
        const clickedSize = this.product.getSizeByLabel(label);
        if (clickedSize && clickedSize.parentSku) {
            this.selectedSkuId = clickedSize.parentSku.skuId;
        }
    };

    public sanitizeSelection = (selection: string) => {
        return selection.replace(/\s/g, ' ').split(' ').join('_');
    };

    public urlUpdateState = (searchKey: string, searchValue: string) => {
        const search = (<Location>root.location).search;
        const pathName = (<Location>root.location).pathname;
        const urlParams = new URLSearchParams(search);
        const sanitizedValue = this.sanitizeSelection(searchValue);
        urlParams.set(searchKey, sanitizedValue);
        window.history.replaceState({}, '', `${pathName}?${urlParams.toString()}`);
    };

    public clearURLState = (searchKey: string) => {
        const search = (<Location>root.location).search;
        const urlParams = new URLSearchParams(search);
        urlParams.delete(searchKey);
        let urlNewState = `${window.location.pathname}`;
        const queryParams = urlParams.toString();
        if (queryParams !== '') {
            urlNewState = urlNewState.concat(`?${queryParams}`);
        }
        window.history.replaceState({}, '', `${urlNewState}`);
    };

    public handleShadeSelect = (shadeName: string) => {
        const currentSelectedShade = this.selectedSku.selectedShade.name;
        this.selectShade(shadeName);
        const newSelectedShade = this.selectedSku.selectedShade.name;

        if (currentSelectedShade !== newSelectedShade) {
            this.emitProductSkuSelected();
        }
    };

    public updateUrl = (param: string, value: string) => {
        if (value) {
            this.urlUpdateState(param, value);
        } else {
            this.clearURLState(param);
        }
    };

    public handleSizeSelect = (label: string) => {
        const currentSelectedSize = this.selectedSku.selectedSize.value;
        this.selectSize(label);
        const newSelectedSize = this.selectedSku.selectedSize.value;

        if (newSelectedSize !== currentSelectedSize) {
            this.emitProductSkuSelected();
        }
    };

    public emitProductSkuSelected() {
        const { skuId, ingredients, perlgem } = this.selectedSku;
        const { SKU_BASE_ID: skuBaseId } = perlgem;
        emit(events.PRODUCT_SKU_SELECTED, { skuId, payload: { ingredients, skuBaseId } });
    }

    public handleFavoriteClick = () => {
        emit(events.FAVORITE_SELECTED, {
            skuBaseId: this.selectedSku.perlgem.SKU_BASE_ID
        });
    };

    public emitSocialShareIconTriggered = (icon?: string) => {
        emit(events.SOCIAL_SHARE_ICON_TRIGGERED, { eventLabel: icon });
    };

    private enhanceImageListAltText(images: IAsset[]) {
        let skuImages: IAsset[] = [];
        skuImages = images.map((image, index) => {
            const isFirstImage = !index;
            if (isFirstImage) {
                return this.enhanceImageAltText(image);
            }

            const img = { ...image };
            img.alt = '';

            return img;
        });

        return skuImages;
    }

    private enhanceImageAltText(image: IAsset) {
        const img = { ...image };
        img.alt = `${this.altTextPrefix} | ${img.alt}`.toLowerCase().trim();

        return img;
    }

    @computed
    get selectedProductUrl() {
        const { product, selectedSku } = this;

        if (selectedSku) {
            if (selectedSku.selectedShade && selectedSku.selectedShade.name) {
                const shadeParam = this.sanitizeSelection(selectedSku.selectedShade.name);

                return `${product.productUrl}?${
                    ProductBaseController.shadeUrlParam
                }=${encodeURIComponent(shadeParam)}`;
            }
            if (selectedSku.selectedSize && selectedSku.selectedSize.value) {
                const sizeParam = this.sanitizeSelection(selectedSku.selectedSize.value);

                return `${product.productUrl}?${
                    ProductBaseController.sizeUrlParam
                }=${encodeURIComponent(sizeParam)}`;
            }
        }

        return product.productUrl;
    }

    @computed
    public get isMobile() {
        return window.innerWidth < Breakpoints.desktop;
    }

    public navigateToProduct = async () => {
        // check for query params and push them into history
        const search = queryParamUtils.getQueryParams();
        if (search.length) {
            window.history.pushState({}, '');
        }
        const positionIndex = this.positionIndex;
        const { skuId } = this.selectedSku;
        const product = this.product;
        const navigateToProductUseCase = new ProductSelectedUseCase(
            this.selectedProductUrl,
            skuId,
            positionIndex,
            product
        );
        await navigateToProductUseCase.execute();
    };
    public handleLiveChatCTAClick = (liveChatComponentType: LiveChatComponentTypes) => {
        emit(events.LIVE_CHAT_INTERFACE_CLICKED, {
            eventLabel: liveChatComponentType
        });
    };

    // Auto select the best shade match recommended by VTO
    public selectBestShade = async (isSPP: boolean = false) => {
        const { hasColorExperience, hasFoundationExperience, productId } = this.product;
        if (!hasColorExperience && !hasFoundationExperience) {
            return;
        }

        const bestMatchSkuId = await query(serviceQueries.GET_BEST_MATCH_SKU_ID, { productId });

        const bestMatchSku =
            bestMatchSkuId && this.product.skus.find((sku) => sku.skuId === bestMatchSkuId);

        if (bestMatchSku) {
            const { skuId, selectedShade } = bestMatchSku;
            const { name: shadeName } = selectedShade;

            if (isSPP) {
                this.handleShadeSelect(shadeName);
                if (!this.isMobile) {
                    this.updateUrl(ProductBaseController.shadeUrlParam, shadeName);
                }
            }

            this.setSelectedSkuId(skuId);
            selectedShade.setBestShade();
        }
    };

    @action
    private updateFavorites = (favoriteSkuBaseIds: number[], isFavorite: boolean = true) => {
        this.product.initialSkus = this.product.skus.map((sku) => {
            const {
                perlgem: { SKU_BASE_ID: skuBaseId }
            } = sku;
            if (favoriteSkuBaseIds.includes(skuBaseId)) {
                sku.isFavorite = isFavorite;
            }

            return sku;
        });
    };

    @action
    public setIsProductImageEnabled = (isEnabled: boolean) => {
        this.isProductImageEnabled = isEnabled;
    };

    @action
    public toggleDescription = () => {
        this.isDescriptionExpanded = !this.isDescriptionExpanded;
    };

    @on(events.FAVORITES_LOADED)
    public initFavorites = ({ skuBaseIds }: IFavorites) => {
        this.updateFavorites(skuBaseIds);
    };

    @on(events.FAVORITE_STATUS_UPDATED)
    public updateFavorite = ({
        skuBaseId,
        isFavorite,
        isAlreadyAdded
    }: IFavoriteUpdatedEventData) => {
        if (!isAlreadyAdded) {
            this.updateFavorites([skuBaseId], isFavorite);
        }
    };
}
